import React, { useState, useMemo, useRef, useContext } from 'react';
import { Collapse, FormGroup, Card, CardBody } from 'src/components/ui';
import { getObject } from '../../util/mapObject';
import { useSideChannelSubscription } from '../../util/useSideChannel';
import xor from '../../util/xor';
import Jnx from '../../util/jnx';
import getPathFromId from '../../util/getPathFromId';
import AnimateHeight from '../ui/AnimateHeight';
import TitleField from './ObjectFieldTemplate/TitleField';
import { get } from 'lodash';
import { AuthContext } from 'src/context/AuthContext';

function HideObjectField(props) {
  const auth = useContext(AuthContext);
  const {
    name,
    idPrefix,
    idSeparator,
    registry,
    disabled,
    readonly,
    hideError,
    onBlur,
    onFocus,
    onChange,
    uiSchema,
    errorSchema,
    fieldPathId,
    schema,
    value,
    formData: _formData,
  } = props;
  const formContext = registry?.formContext;
  const { sideChannel } = formContext;
  const { title, properties: childFields } = schema;

  const formData = value ?? _formData ?? {};

  const {
    fields: { SchemaField },
  } = registry;

  const { $id } = fieldPathId;

  const {
    'ui:className': className,
    'ui:newSection': uiNewSection,
    'ui:sectionType': uiSectionType,
    'ui:fromBottom': sectionFromBottom,
    'ui:expandable': expandable,
    'ui:expandedDefault': expandedDefault = true,
    'ui:labelClassName': labelClassName = '',
    'ui:asCard': asCard = false,
  } = uiSchema;

  const [wasPropertyKeyModified, setWasPropertyKeyModified] = useState();

  const [rootFormData, formObject] = useSideChannelSubscription(sideChannel, [0, 1]) || [{}];

  const formDataRef = useRef();
  formDataRef.current = formData;

  function onKeyChange(oldValue, value, es) {
    if (oldValue === value) {
      return;
    }

    value = getAvailableKey(value, formData);
    const _formData = formDataRef.current || {};
    const newKeys = { [oldValue]: value };
    const renamed = Object.keys(_formData).reduce((_, key) => {
      _[newKeys[key] || key] = _formData[key];

      return _;
    }, {});

    setWasPropertyKeyModified(true);

    onChange(renamed, fieldPathId.path, es && props.errorSchema && { ...props.errorSchema, [value]: es });
  }

  function onDropPropertyClick(key) {
    return event => {
      event.preventDefault();
      const copiedFormData = { ...formDataRef.current };
      delete copiedFormData[key];
      onChange(copiedFormData, fieldPathId.path);
    };
  }

  const route = useMemo(() => getPathFromId($id), [$id]);

  const bindings = useMemo(
    () => ({
      formContext,
      formObject,
      rootFormData,
      auth,
    }),
    [formContext, formObject, rootFormData, auth]
  );

  const [visibleCount, hide] = useMemo(() => {
    const { showCount, nonEmptyCount, emptyChecks, aloneChecks } = Object.keys(childFields || {}).reduce(
      (_, name) => {
        const childUISchema = uiSchema[name] || {};

        if (childUISchema['ui:showIf']) {
          const response = evalShowIf(rootFormData, route, childUISchema['ui:showIf'], bindings);

          if (response) {
            _.showCount += 1;
          } else {
            _.emptyChecks[name] = 1;
          }
        } else {
          _.showCount += 1;
        }

        return _;
      },
      { showCount: 0, nonEmptyCount: 0, emptyChecks: {}, aloneChecks: [] }
    );

    const isEmpty = nonEmptyCount === 0;

    return [
      showCount,
      {
        ...emptyChecks,
        ...aloneChecks.reduce((_, [c, inverLogic]) => {
          _[c] = xor(isEmpty, inverLogic);

          return _;
        }, {}),
      },
    ];
  }, [childFields, rootFormData, uiSchema, bindings]);

  const [expanded, setExpanded] = useState(expandedDefault);

  function toggleExpand(e) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    setExpanded(!expanded);
  }

  const childSchemas = useMemo(
    () =>
      Object.entries(childFields || {}).map(([name, fieldDef]) => ({
        ...fieldDef,
        _key: name,
      })),
    [childFields]
  );

  const isRootField = $id === 'root';
  const searchBarAppend = isRootField ? formContext?.searchBarAppend : null;

  const childSchemaFields = (
    <AnimateHeight height="auto" duration={150} className="animate-height">
      {(expanded || !expandable) && (
        <div className="prop-fields">
          {childSchemas.map(fieldDef =>
            hide[fieldDef._key] ? null : (
              <SchemaField
                key={fieldDef._key}
                name={fieldDef._key}
                required={isRequired(fieldDef._key, schema, hide[fieldDef._key], uiSchema)}
                schema={fieldDef}
                uiSchema={get(uiSchema, fieldDef._key.split('.'))}
                errorSchema={(errorSchema || {})[fieldDef._key]}
                fieldPathId={{ $id: `${fieldPathId.$id}_${fieldDef._key}`, path: [...fieldPathId.path, fieldDef._key] }}
                formContext={formContext}
                value={(formData || {})[fieldDef._key]}
                formData={(formData || {})[fieldDef._key]}
                wasPropertyKeyModified={wasPropertyKeyModified}
                onKeyChange={(value, errorSchema) => onKeyChange(fieldDef._key, value, errorSchema)}
                onChange={(value, path, es, id) => {
                  // In RJSF v6, ObjectField passes changes through without merging.
                  // Standard fields (StringField, NumberField) inject their absolute path.
                  // Custom fields that call onChange(value) without a path need us to compute it.
                  const effectivePath = path && path.length > 0 ? path : [...fieldPathId.path, fieldDef._key];
                  onChange(value, effectivePath, es, id);
                }}
                onBlur={onBlur}
                onFocus={onFocus}
                registry={registry}
                disabled={disabled}
                readonly={readonly}
                hideError={hideError}
                onDropPropertyClick={onDropPropertyClick}
              />
            )
          )}
          {searchBarAppend}
        </div>
      )}
    </AnimateHeight>
  );

  const titleField = (
    <TitleField
      id={$id}
      className={labelClassName}
      isSection={uiNewSection}
      sectionType={uiSectionType}
      sectionFromBottom={sectionFromBottom}
      title={title}
      bindings={bindings}
      expandable={expandable}
      expanded={expanded}
      toggleExpand={toggleExpand}
    />
  );

  let field = (
    <FormGroup className={`rjsf-form-group ${className ? ` ${className}` : ''}`}>
      {sectionFromBottom ? (
        <>
          {childSchemaFields}
          {titleField}
        </>
      ) : (
        <>
          {titleField}
          {childSchemaFields}
        </>
      )}
    </FormGroup>
  );

  if (asCard) {
    field = (
      <Card className="card-shadow">
        <CardBody>{field}</CardBody>
      </Card>
    );
  }

  return visibleCount === 0 ? null : field;
}

export function compileShowIf(objectShowIf) {
  return objectShowIf instanceof Jnx ? objectShowIf : new Jnx(objectShowIf);
}

export function evalShowIf(formData, route, objectShowIf, bindings) {
  try {
    return compileShowIf(objectShowIf).eval(formData, route, bindings);
  } catch {
    return false;
  }
}

export function getAvailableKey(preferredKey, formData) {
  let index = 0;
  let newKey = preferredKey;

  while (Object.prototype.hasOwnProperty.call(formData, newKey)) {
    index += 1;
    newKey = ''.concat(preferredKey, '-').concat(index);
  }

  return newKey;
}

export function isRequired(name, schema, hideField, uiSchema) {
  const childUISchema = uiSchema[name] || {};

  return (Array.isArray(schema.required) && schema.required.indexOf(name) !== -1 && !hideField) || (childUISchema['akc:required'] && !hideField);
}

export default HideObjectField;
